import { useEffect } from 'react';
import { WellVersions } from './types';
import style from './version-dropdown.module.scss';
import { Dropdown, MultiSelect, TextInputSkeleton } from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { Rotate as ResetIcon } from '@carbon/react/icons';

export const VersionDropdown = ({
  versions,
  selectedVersions,
  setSelectedVersions,
  title = '',
  isLoading = false,
  isError = false,
  onRefetch,
  isMultiSelect = true,
}: {
  versions: WellVersions;
  selectedVersions: WellVersions;
  setSelectedVersions: (versionIds: WellVersions) => void;
  isMultiSelect?: boolean;
  title?: string;
  isLoading?: boolean;
  isError?: boolean;
  onRefetch?: () => void;
}) => {
  useEffect(
    () => {
      if (selectedVersions.length === 0 && versions.length > 0) {
        setSelectedVersions([versions[0]]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versions.length]
  );

  if (isLoading) {
    return (
      <div className={style.versionSelectorSkeleton}>
        <TextInputSkeleton hideLabel data-testid="version-dropdown-skeleton" />
      </div>
    );
  }

  const dropdownItems = versions.map((item) => ({
    ...item,
    id: item.versionId,
  }));

  const selectedDropdownItems = dropdownItems.filter((item) =>
    selectedVersions.find((v) => v.versionId === item.id)
  );

  const handleOnChange = (event: { selectedItems: typeof dropdownItems }) => {
    const selected = event.selectedItems;
    const selectedVersions = versions.filter((version) =>
      selected.find((selectedItem) => selectedItem.id === version.versionId)
    );
    setSelectedVersions(selectedVersions);
  };

  const dropdownLabel = (() => {
    if (isError) {
      return 'Error fetching versions';
    } else if (versions.length === 0) {
      return 'No versions available';
    } else if (selectedDropdownItems.length === 0) {
      return 'Please select a version';
    } else if (selectedDropdownItems.length === 1) {
      return selectedDropdownItems[0].label;
    } else {
      return 'Selected Versions';
    }
  })();

  return (
    <div className={style.versionDropdown}>
      <div className={style.dropdownActionContainer}>
        <div className={style.dropDown} data-testid="version-dropdown">
          {isMultiSelect ? (
            <MultiSelect
              invalid={isError}
              titleText={title}
              size="lg"
              id="version-multiselect"
              label={dropdownLabel}
              items={dropdownItems}
              itemToString={(item) => {
                return item ? `${item.label}` : '';
              }}
              selectedItems={selectedDropdownItems}
              onChange={handleOnChange}
              selectionFeedback="fixed"
              sortItems={(items) => [...items]}
            />
          ) : (
            <Dropdown
              invalid={isError}
              titleText={title}
              size="lg"
              id="version-dropdown"
              items={dropdownItems}
              itemToString={(item) => {
                return item ? `${item.label}` : '';
              }}
              label={dropdownLabel}
              selectedItem={selectedDropdownItems[0]}
              onChange={({ selectedItem }) => {
                if (selectedItem) {
                  handleOnChange({ selectedItems: [selectedItem] });
                }
              }}
            />
          )}
        </div>
        {isError && (
          <div className={style.refetchButton}>
            <TooltipIcon
              data-testid="version-dropdown-refetch"
              definition="Click to refetch the versions"
              onClick={() => {
                onRefetch?.();
              }}
            >
              <ResetIcon className={style.resetIcon} />
            </TooltipIcon>
          </div>
        )}
      </div>
    </div>
  );
};
